import {useLoadSpacingScenario} from '../../fields'

import {buildSlotRequest} from './buildSlotRequest'
import {useSlotFormData} from './useSlotFormData'

/**
 * Options for useSlotRequest hook
 */
export interface UseSlotRequestOptions {
  /** Organization unit ID */
  orgUnitId: string
  /** Site number for delivery */
  siteNumber?: string
  /** Optional: existing order ID (for Order Management) */
  orderId?: string
  /** Whether to normalize date to 12:00 PM UTC (true for availability, false for validate) */
  normalizeDate?: boolean
  /** Fallback contract number when form contract field is absent (e.g. empty options list) */
  contractNumber?: string
  /** Fallback contract item position number when form contract field is absent */
  contractItemPositionNumber?: string
  /** Optional resolver for replacing the form dateTime before request params are built */
  resolveDateTime?: (currentDateTime: string) => string
}

/**
 * Hook to build slot request from form data.
 * Combines form data with load spacing scenario and provided options.
 *
 * @param options - Configuration including orgUnitId, siteNumber, orderId, and date selection
 * @returns Slot request result with validation status and params
 *
 * @example
 * ```tsx
 * // Using date at index 0 from form
 * const request = useSlotRequest({
 *   orgUnitId: '026',
 *   siteNumber: '1234',
 *   dateIndex: 0
 * })
 *
 * // Using custom date
 * const request = useSlotRequest({
 *   orgUnitId: '026',
 *   dateTime: '2024-12-05T12:00:00Z'
 * })
 * ```
 */
export const useSlotRequest = (
  options: UseSlotRequestOptions & ({dateIndex: number} | {dateTime: string})
) => {
  const formData = useSlotFormData()
  const scenario = useLoadSpacingScenario()

  return buildSlotRequest(formData, {
    ...options,
    loadSpacingScenario: scenario
  })
}

/**
 * Hook to build slot requests for all dateTimes in the form
 * Uses buildSlotRequest directly for each dateTime
 */
export const useSlotRequests = (options: UseSlotRequestOptions) => {
  const formData = useSlotFormData()
  const scenario = useLoadSpacingScenario()
  const {dateTime} = formData

  return dateTime.map((_, index) =>
    buildSlotRequest(formData, {
      ...options,
      loadSpacingScenario: scenario,
      dateIndex: index
    })
  )
}
